import React, { useEffect, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import Autocomplete from '@mui/material/Autocomplete'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import DualActionButtons from '../../components/action_button/DualActionButtons'
import {
  dialogActionButtonSx,
  dialogActionsSpacedSx,
  dialogAvatarSx,
  dialogContentWithBorderSx,
  dialogCurrentRoleLabelSx,
  dialogEmailTextSx,
  dialogFieldLabelSx,
  dialogHeaderWithSubtitleSx,
  dialogNameTextSx,
  dialogSearchFieldSx,
  dialogSearchIconSx,
  dialogSubtitleSx,
  dialogTitleSx,
  dialogUserCardSx,
  getDialogCancelAction,
  getDialogPaperSx
} from '../../constants/dialogSx'
import {
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_HOVER,
  UI_COLOR_TEXT_DIALOG_TITLE
} from '../../constants/uiColors'
import { RbacRoleChip } from './RbacChips'
import {
  getRbacUserDisplayName,
  getRbacUserInitials,
  getRbacUserPrimaryRoleName
} from '../pages/shared/rbacUserDetailHelpers'
import RbacDialogFieldLabel from './shared/RbacDialogFieldLabel'

const dialogPaperSx = getDialogPaperSx(560)

const reassignFieldLabelSx = {
  ...dialogFieldLabelSx,
  color: UI_COLOR_TEXT_DIALOG_TITLE
}

const reassignAvatarSx = {
  ...dialogAvatarSx,
  width: 40,
  height: 40
}

const currentRoleWrapSx = {
  flexShrink: 0,
  textAlign: 'right'
}

const currentRoleLabelSx = {
  ...dialogCurrentRoleLabelSx,
  mb: 0.5
}

export default function RbacReassignUserDialog({
  open,
  onClose,
  onExited,
  onSubmit,
  user,
  roles = [],
  submitting = false
}) {
  const [selectedRole, setSelectedRole] = useState(null)

  useEffect(() => {
    if (!open) return
    setSelectedRole(null)
  }, [open, user?.id])

  const displayName = getRbacUserDisplayName(user)
  const primaryRoleName = getRbacUserPrimaryRoleName(user)
  const hasUnassignedRole = primaryRoleName === 'SIN ROL'
  const canSubmit = Boolean(selectedRole) && !submitting && roles.length > 0

  const handleSubmit = () => {
    if (!canSubmit) return
    onSubmit?.(selectedRole.id)
  }

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      TransitionProps={{ onExited }}
      fullWidth
      maxWidth={false}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={dialogHeaderWithSubtitleSx}>
        <Typography component="span" sx={dialogTitleSx}>
          Reasignar usuario
        </Typography>
        <Typography sx={dialogSubtitleSx}>
          Selecciona el rol al que quieres mover a este usuario.
        </Typography>
      </DialogTitle>
      <DialogContent sx={dialogContentWithBorderSx}>
        <Box sx={dialogUserCardSx}>
          <Avatar sx={reassignAvatarSx}>{getRbacUserInitials(displayName)}</Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={dialogNameTextSx}>{displayName}</Typography>
            <Typography sx={dialogEmailTextSx}>{user?.email || '—'}</Typography>
          </Box>
          <Box sx={currentRoleWrapSx}>
            <Typography sx={currentRoleLabelSx}>Rol actual</Typography>
            <RbacRoleChip
              label={hasUnassignedRole ? 'SIN ROL' : primaryRoleName}
              unassigned={hasUnassignedRole}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <RbacDialogFieldLabel htmlFor="reassign-rbac-user-role" sx={reassignFieldLabelSx}>
            Nuevo rol:
          </RbacDialogFieldLabel>
          <Autocomplete
            options={roles}
            value={selectedRole}
            size="small"
            fullWidth
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, nextRole) => setSelectedRole(nextRole)}
            noOptionsText="No hay roles disponibles"
            renderInput={(params) => (
              <TextField
                {...params}
                id="reassign-rbac-user-role"
                placeholder="Busque o seleccione un rol"
                size="small"
                hiddenLabel
                InputLabelProps={{ ...params.InputLabelProps, shrink: false }}
                sx={dialogSearchFieldSx}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start" sx={{ mr: 0.5 }}>
                        <SearchIcon sx={dialogSearchIconSx} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={dialogActionsSpacedSx}>
        <DualActionButtons
          fullWidthOnMobile={false}
          minWidth={112}
          gap={1}
          secondaryAction={getDialogCancelAction({
            onClick: onClose,
            disabled: submitting,
            sx: dialogActionButtonSx
          })}
          primaryAction={{
            label: 'Reasignar',
            loadingLabel: 'Reasignando...',
            icon: null,
            color: UI_COLOR_PRIMARY,
            hoverColor: UI_COLOR_PRIMARY_HOVER,
            onClick: handleSubmit,
            disabled: !canSubmit,
            loading: submitting,
            sx: dialogActionButtonSx
          }}
        />
      </DialogActions>
    </Dialog>
  )
}
